/**
 * The three states every vertical-operation widget (api#1141) draws, in one
 * place: a read in flight (pulse), a read that failed or was refused (a
 * load-failure notice, never "none"), and an envelope with no payload (also a
 * failure: null is not an empty list). Only a delivered payload reaches
 * `children`.
 */
import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { WidgetPending } from './WidgetPanel';

export function OpsWidgetRead<T>({ q, subject, allowed, children }: {
  q: UseQueryResult<T | null, unknown>;
  subject: string;
  allowed: string;
  children: (data: T) => ReactNode;
}) {
  const failure = q.data === undefined ? queryLoadFailure(q) : null;
  if (failure) return <LoadFailureNotice error={queryFailureError(q)} kind={failure} subject={subject} allowed={allowed} className="py-6" />;
  if (q.data === undefined) return <WidgetPending rows={3} />;
  if (q.data === null) return <LoadFailureNotice error={new Error(`No ${subject} payload`)} kind="unreachable" subject={subject} className="py-6" />;
  return <>{children(q.data)}</>;
}

/**
 * The widget's vertical is off for this company. The API would answer 403
 * ("vertical not enabled"), which is not a permission refusal, so the read is
 * never made and this says what is true instead.
 */
export function VerticalOff({ testId, children }: { testId: string; children: ReactNode }) {
  return <p data-testid={testId} className="py-6 text-center text-sm text-fieldiq-muted">{children}</p>;
}

/** A saturated badge with dark ink: the one badge idiom on the Overview. */
export function OpsBadge({ fill, children, testId }: { fill: string; children: ReactNode; testId?: string }) {
  return (
    <span data-testid={testId} className={`whitespace-nowrap rounded-fi-pill border border-black/20 px-1.5 py-px text-[0.65rem] font-black uppercase tracking-wide text-fi-signal-ink ${fill}`}>
      {children}
    </span>
  );
}

/** A row that opens its record. Rows with no link are plain, never a dead button. */
export function OpsRow({ to, onOpen, children, testId }: { to: string | null; onOpen: (to: string) => void; children: ReactNode; testId?: string }) {
  const cls = 'flex w-full items-start justify-between gap-3 px-3 py-2 text-left text-xs';
  return to ? (
    <button type="button" data-testid={testId} onClick={() => onOpen(to)} className={`${cls} transition-colors hover:bg-fieldiq-dark/50`}>
      {children}
    </button>
  ) : (
    <div data-testid={testId} className={cls}>{children}</div>
  );
}

export const dash = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));
